// The Ask AI bar above every page's table — the Worklist, History, Shipping, Pre-route, the System
// log and Admin's tabs (Import apart): the chat widget's engine (`ai-chat.ts`) with its own
// conversation, for setting the page in words and short answers. A request that names lines
// sets the grid in one step (`apply_view`); a question is answered in a line or two and leaves the
// grid as it is. It is also the one place for Undo, whichever of the bar or the panel set the view.

import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import type { FormEvent } from "react";
import { Loader2Icon, MessageSquareShareIcon, SparklesIcon, SquareIcon, Undo2Icon, XIcon } from "lucide-react";
import {
  ADMIN_SUGGESTIONS,
  HISTORY_SUGGESTIONS,
  PRE_ROUTE_SUGGESTIONS,
  SHIPPING_SUGGESTIONS,
  SYSTEM_LOG_SUGGESTIONS,
  WORKLIST_SUGGESTIONS,
} from "@app/shared";
import type { GridView, Mode, ViewStep } from "@app/shared";
import { Button, Input } from "@axny/platform-ui";
import type { ChatMessage } from "./ai-chat";
import { askTurn } from "./ai-chat";
import { InlineMarkdown } from "./inline-markdown";
import { continueInChat } from "./axai";

/** The view Ask AI set (`apply_view`), while it may be undone: Undo, and what it could not apply. */
export type AppliedStep = ViewStep<GridView>;

/** Admin's tabs with the bar (Import has none). */
export type AdminBarTab = "routing-rules" | "assignments" | "customers" | "divisions";

/**
 * The pages with the bar. Shipping and Pre-route set nothing (no Undo); the System log and Admin's
 * tabs set their own filters, with Undo.
 */
export type BarPage = Mode | "shipping" | "pre-route" | "system-log" | `admin-${AdminBarTab}`;

/** Each page asks about its own rows; a phone (under Tailwind's `sm`) gets the short one. */
const PLACEHOLDER: Record<BarPage, { wide: string; phone: string }> = {
  shipping: { wide: "Ask AI: e.g. what ships tomorrow?", phone: "Ask AI: e.g. ships tomorrow" },
  worklist: {
    wide: "Ask AI: e.g. Walmart not routed, sorted by cancel date",
    phone: "Ask AI: e.g. Walmart not routed",
  },
  history: { wide: "Ask AI: e.g. Belk shipped since August", phone: "Ask AI: e.g. Belk shipped" },
  "pre-route": { wide: "Ask AI: e.g. how many pre-routes alert?", phone: "Ask AI: e.g. alerts" },
  "system-log": { wide: "Ask AI: e.g. what did Dijana change yesterday?", phone: "Ask AI: e.g. changes today" },
  "admin-routing-rules": {
    wide: "Ask AI: e.g. which customers have no routing rule?",
    phone: "Ask AI: e.g. no routing rule",
  },
  "admin-assignments": { wide: "Ask AI: e.g. who looks after Belk?", phone: "Ask AI: e.g. who has Belk?" },
  "admin-customers": {
    wide: "Ask AI: e.g. which Deposco partners are shown as Amazon?",
    phone: "Ask AI: e.g. shown as Amazon",
  },
  "admin-divisions": {
    wide: "Ask AI: e.g. which divisions are abbreviated?",
    phone: "Ask AI: e.g. abbreviated divisions",
  },
};

/** The bar's starter questions per page (`ai-guidance.ts`): three at most. */
const SUGGESTIONS: Record<BarPage, readonly string[]> = {
  worklist: WORKLIST_SUGGESTIONS,
  history: HISTORY_SUGGESTIONS,
  shipping: SHIPPING_SUGGESTIONS,
  "pre-route": PRE_ROUTE_SUGGESTIONS,
  "system-log": SYSTEM_LOG_SUGGESTIONS,
  "admin-routing-rules": ADMIN_SUGGESTIONS["routing-rules"],
  "admin-assignments": ADMIN_SUGGESTIONS.assignments,
  "admin-customers": ADMIN_SUGGESTIONS.customers,
  "admin-divisions": ADMIN_SUGGESTIONS.divisions,
};
const PHONE = "(max-width: 639px)";

/** Whether the screen is phone-width now, following a resize or rotation. */
function usePhone(): boolean {
  return useSyncExternalStore(
    (changed) => {
      const mq = window.matchMedia(PHONE);
      mq.addEventListener("change", changed);
      return () => mq.removeEventListener("change", changed);
    },
    () => window.matchMedia(PHONE).matches,
  );
}

/** Told to the model on every bar question on a grid page, after the page's own system prompt. */
const GRID_BAR_PROMPT =
  "This was typed in the Ask AI bar above the grid, not in the chat panel. Answer in one or two short lines of " +
  "plain text: no lists, no headings. Two kinds of input:\n" +
  "1. A request that names lines (a customer, status, warehouse, tab, date range or sort) sets the grid with " +
  "apply_view in one call, with or without a word like show, list or find. 'lines due this week', 'Walmart not " +
  "routed' and 'past deadline at PE, sorted by cancel date' each mean the same as 'show lines due this week'. Then " +
  "reply in one line with its lines_shown, e.g. 'Showing 12 Walmart lines not routed, sorted by cancel date.', and " +
  "say what came back under not_applied. A grouping the grid cannot show ('… by customer') still sets the grid, " +
  "and the answer also gives the top counts per value from query_worklist or query_history with group_by.\n" +
  "2. Only a real question (how many, what, which, why, when, who, is there…) is answered from the query tools and " +
  "leaves the grid as it is: no apply_view. E.g. 'how many lines are past deadline?', 'which customer has the most " +
  "lines due this week?'. Its count is within the grid's view: say the scope, and the count without the view " +
  "when say_without_view is true.\n" +
  "'Which lines must be routed today?' is both: set the grid to Past deadline + Route now (apply_view) and say " +
  "how many, from its lines_per_tab, as the page says it: '106 lines to route now or late: 79 past deadline, 27 " +
  "route now.'";

const SHORT =
  "This was typed in the Ask AI bar above the page, not in the chat panel. Answer in one or two short lines of " +
  "plain text: no lists, no headings. Take every number from a tool answer.";

/** What the bar tells the model on each page: grid pages set the grid; the others read, or set their own filters. */
function barPrompt(page: BarPage): string {
  if (page === "worklist" || page === "history" || page === "shipping") return GRID_BAR_PROMPT;
  if (page === "pre-route") return `${SHORT} Answer from query_pre_routes; nothing on this page is set from the bar.`;
  if (page === "system-log")
    return (
      `${SHORT} Two kinds of input:\n` +
      "1. A request that names entries (a person, an action, dates or a search) — 'Meir's changes this week', " +
      "'show approvals today' — sets the log with apply_view in one call; reply in one line with its " +
      "entries_shown, e.g. 'Showing 14 changes by Meir Cohen this week.', and say what came back under not_applied.\n" +
      "2. A real question (how many, who, what, when…) is answered from query_system_log and leaves the log as it is."
    );
  return (
    `${SHORT} Two kinds of input:\n` +
    "1. A request that names rows the tab can show with its own search box, filter buttons or views sets the tab " +
    "with apply_view in one call; reply in one line with rows_shown, and say what came back under not_applied. " +
    "When its answer says opened (the filters were another tab's), start with it: 'Opened Routing rules: 106 " +
    "customers with no window and open lines.'; when it gives open_tab, say the links under the bar open them.\n" +
    "2. A real question is answered from the query tools and leaves the tab as it is. Name the tab and say each row " +
    "as the tab shows it, e.g. 'Belk — Owner Julissa, Backup —; confirm pending.'"
  );
}

interface Answer {
  question: string;
  text: string;
  error?: boolean;
  /** The grid's view when it was answered: the answer goes once the reader changes it. */
  version: string;
}

export function AskAiBar({
  mode,
  version,
  step,
  onBegin,
  onEnd,
  onUndo,
}: {
  mode: BarPage;
  /** The grid's view now, as `AppliedStep.version` says it. */
  version: string;
  /** Ask AI's last step (bar or panel), while it may still be undone. */
  step: ViewStep<unknown> | null;
  /** A bar question starts and ends: Undo of all it sets goes back to before it. */
  onBegin: () => void;
  onEnd: () => void;
  onUndo: () => void;
}) {
  const phone = usePhone();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [answer, setAnswer] = useState<Answer | null>(null);
  // The bar's own conversation, for a follow-up: memory only, never the panel's.
  const history = useRef<ChatMessage[]>([]);
  const abort = useRef<AbortController | null>(null);
  const versionRef = useRef(version);
  versionRef.current = version;
  useEffect(() => () => abort.current?.abort(), []);

  // The widget's starter questions (`index.html`) that are this page's, in its order; Admin's are the
  // bar's own (managers only: never in the panel's intro, which every person sees).
  const suggestions = (
    mode.startsWith("admin-")
      ? SUGGESTIONS[mode]
      : (window.AXAI_ASK_EXAMPLES ?? []).filter((q) => SUGGESTIONS[mode].includes(q))
  ).slice(0, 3);

  const ask = async (question: string) => {
    const q = question.trim();
    if (!q || busy) return;
    const controller = new AbortController();
    abort.current = controller;
    setBusy("Thinking…");
    setAnswer(null);
    onBegin();
    try {
      const turn = await askTurn(q, history.current, {
        signal: controller.signal,
        onStatus: (s) => setBusy(s),
        extraSystem: barPrompt(mode),
      });
      history.current = turn.history;
      setText("");
      setAnswer({ question: q, text: turn.text.trim(), version: versionRef.current });
    } catch (e) {
      const stopped = e instanceof DOMException && e.name === "AbortError";
      setAnswer({
        question: q,
        text: stopped ? "Stopped." : `Sorry, something went wrong: ${e instanceof Error ? e.message : String(e)}`,
        error: !stopped,
        version: versionRef.current,
      });
    } finally {
      abort.current = null;
      setBusy(null);
      onEnd();
    }
  };

  const clear = () => {
    setText("");
    setAnswer(null);
    history.current = [];
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void ask(text);
  };

  // The answer stays while the grid is the view it answered; any change by hand takes it away.
  const shown = answer && answer.version === version ? answer : null;
  const undoable = step && step.version === version ? step : null;

  return (
    <div className="space-y-2" data-testid="routing-ask-bar">
      <form role="search" onSubmit={submit} className="flex w-full min-w-0 items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <SparklesIcon
            className="text-primary pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
            aria-hidden
          />
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                clear();
              }
            }}
            maxLength={500}
            placeholder={phone ? PLACEHOLDER[mode].phone : PLACEHOLDER[mode].wide}
            aria-label={
              mode.startsWith("admin-") || mode === "system-log" ? "Ask AI about this page" : "Ask AI about these lines"
            }
            className="h-9 pl-8"
            data-testid="routing-ask-input"
          />
        </div>
        {busy ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => abort.current?.abort()}
            data-testid="routing-ask-stop"
          >
            <SquareIcon aria-hidden />
            Stop
          </Button>
        ) : (
          <Button type="submit" size="sm" disabled={text.trim() === ""} data-testid="routing-ask-submit">
            Ask AI
          </Button>
        )}
      </form>

      {!busy && !shown && !undoable && suggestions.length ? (
        <div className="flex flex-wrap gap-1.5" data-testid="routing-ask-chips">
          {suggestions.map((q) => (
            <Button
              key={q}
              variant="outline"
              size="xs"
              className="h-auto min-h-6 py-0.5 text-left whitespace-normal"
              onClick={() => void ask(q)}
            >
              {q}
            </Button>
          ))}
        </div>
      ) : null}

      {busy || shown || undoable ? (
        <div
          role="status"
          className="bg-muted/40 flex flex-wrap items-start gap-2 rounded-md border px-3 py-2 text-sm"
          data-testid="routing-ask-answer"
        >
          <SparklesIcon className="text-primary mt-0.5 size-4 shrink-0" aria-hidden />
          {/* At least 12rem of text: on a phone the buttons wrap under it rather than squeeze it. */}
          <div className="min-w-0 flex-1 basis-48 space-y-1 [overflow-wrap:anywhere]">
            {busy ? (
              <p className="text-muted-foreground flex items-center gap-2 text-xs" data-testid="routing-ask-busy">
                <Loader2Icon className="size-3.5 animate-spin" aria-hidden />
                {busy}
              </p>
            ) : shown ? (
              <>
                <p className="text-muted-foreground truncate text-xs" data-testid="routing-ask-question">
                  {shown.question}
                </p>
                {shown.error ? (
                  <p className="text-destructive" data-testid="routing-ask-text">
                    {shown.text}
                  </p>
                ) : (
                  // The model's **bold** and lists shown, never their marks.
                  <div data-testid="routing-ask-text">
                    <InlineMarkdown text={shown.text} />
                  </div>
                )}
              </>
            ) : (
              <p data-testid="routing-ask-applied">Ask AI set this view.</p>
            )}
            {!busy && undoable?.unapplied.length ? (
              <p className="text-muted-foreground text-xs" data-testid="routing-ask-unapplied">
                Not applied here: {undoable.unapplied.join("; ")}.
              </p>
            ) : null}
          </div>
          {busy ? null : (
            <div className="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-1">
              {undoable ? (
                <Button variant="ghost" size="sm" onClick={onUndo} data-testid="routing-ask-undo">
                  <Undo2Icon aria-hidden />
                  Undo
                </Button>
              ) : null}
              {shown && !shown.error ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => continueInChat(shown.question, shown.text)}
                  data-testid="routing-ask-continue"
                >
                  <MessageSquareShareIcon aria-hidden />
                  Continue in chat
                </Button>
              ) : null}
              {shown ? (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={clear}
                  aria-label="Close"
                  data-testid="routing-ask-close"
                >
                  <XIcon />
                </Button>
              ) : null}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
